import type { BossCandy } from '@/net/api';

export interface PickupPosition { x: number; y: number }

/** Keep the actual contact position until the server confirms collection.
 * Walking past during an in-flight request must not lose the pickup. */
export class HalloweenCandyCollector {
  private readonly contacts = new Map<string, PickupPosition>();

  constructor(private readonly radius: number, private readonly rottenRadius = 40) {}

  private radiusFor(drop: BossCandy): number { return drop.kind === 'rotten' ? this.rottenRadius : this.radius; }

  observe(drops: readonly BossCandy[], position: PickupPosition, serverNow: number): BossCandy[] {
    const touched: BossCandy[] = [];
    for (const drop of drops) {
      if (drop.kind === 'rotten' && serverNow >= (drop.expiresAt ?? 0)) {
        this.contacts.delete(drop.id); continue;
      }
      if (this.contacts.has(drop.id) || drop.availableAt > serverNow) continue;
      if (Math.hypot(drop.x - position.x, drop.y - position.y) <= this.radiusFor(drop)) {
        this.contacts.set(drop.id, { ...position });
        touched.push(drop);
      }
    }
    return touched;
  }

  get pending(): boolean { return this.contacts.size > 0; }

  batch(drops: readonly BossCandy[], fallback: PickupPosition): { ids: string[]; position: PickupPosition } {
    const position = this.contacts.values().next().value ?? fallback;
    return {
      position,
      // One recorded contact can cover several nearby sweets. The server still
      // validates their issued IDs, availability and distance to this position.
      ids: drops.filter(drop => this.contacts.has(drop.id) &&
        Math.hypot(drop.x - position.x, drop.y - position.y) <= this.radiusFor(drop)).map(drop => drop.id),
    };
  }

  reconcile(drops: readonly BossCandy[]): void {
    const remaining = new Set(drops.map(drop => drop.id));
    for (const id of this.contacts.keys()) if (!remaining.has(id)) this.contacts.delete(id);
  }

  release(ids: readonly string[]): void { ids.forEach(id => this.contacts.delete(id)); }

  clear(): void { this.contacts.clear(); }
}
